import React, { useMemo, useRef } from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  MButton,
  MPageContentContainer,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { MDataTableFilter } from '../../../components/Monetize/DataTable';
import { TEST_ACCOUNT_FILTER_KEY } from '../../../components/Monetize/DataTable/FilterOptions/HideTestAccountFilterOption';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ExternalFilterDef,
  useExternalFilters,
} from '../../../components/Monetize/DataTableNew/useExternalFilters';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { useQuotesTableData } from '../../../hooks/AccountDetail';
import { useACL } from '../../../services/acl/acl';
import { getIsTrulyEmptyList } from '../../../utils';
import {
  QUOTE_LIST_CATALOG,
  QuoteListColumnContext,
} from './quoteList.columns';
import { useCanCreateQuote } from './useCanCreateQuote';

const QUOTE_LIST_FILTER_DEFS: ExternalFilterDef[] = [
  { key: 'collaborationAccess', columnHeader: 'Shared with me' },
  { key: TEST_ACCOUNT_FILTER_KEY, columnHeader: 'Hide Test Account' },
];

const QuoteListMain = () => {
  const {
    quoteList,
    loading,
    searchKey,
    searchTerm,
    setSearchTerm,
    pager,
    setPager,
    actionBodyTemplate,
    handleEditQuote,
    handleReviewQuote,
    filterOptions,
    filters,
    setFilters,
    onResetFilter,
    EmptyQuotesPlaceholder,
    isGuidedQuotingAvailable,
    refetch,
  } = useQuotesTableData();

  const { canDo } = useACL();
  const canReadQuoteOwner = canDo([['quote_owner', 'read']]);

  const canCreateQuote = useCanCreateQuote();

  const tableRef = useRef<DataTableRef>(null);

  const onResetFilters = () => {
    tableRef.current?.resetFilters();
    onResetFilter();
  };

  const {
    initialFilters,
    onFiltersChange,
    extraActiveFilters,
    externalFilters,
    setExternalFilters,
  } = useExternalFilters({
    filters,
    setFilters,
    pager,
    setPager,
    filterDefs: QUOTE_LIST_FILTER_DEFS,
  });

  const columnContext = useMemo<QuoteListColumnContext>(
    () => ({ actionBodyTemplate, canCreateQuote, canReadQuoteOwner }),
    [actionBodyTemplate, canCreateQuote, canReadQuoteOwner],
  );
  const { columns, state: columnState } = useTableColumns(
    QUOTE_LIST_CATALOG,
    columnContext,
  );

  const filterComponentReset = React.useRef<any>(null);

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: quoteList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  return (
    <MPageContentContainer>
      <MPageHeader title="Quotes" size="md" childrenProps={{ gap: 2 }}>
        {!isTrulyEmptyList && (
          <>
            <MPageSearchInput
              placeholderKey="Quote Name"
              value={searchTerm}
              count={quoteList?.totalElements}
              mode="alwaysExpanded"
              onChange={setSearchTerm}
            />
            <RefreshTableButton refetch={refetch} />
            <MDataTableFilter
              filters={externalFilters}
              filterOptions={filterOptions}
              setFilters={setExternalFilters}
              onResetFilter={onResetFilters}
              resetFilter={filterComponentReset}
            />

            <ExportEntityButton
              entity="quotes"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
            {canCreateQuote && (
              <>
                <MButton variant="secondary" asChild>
                  <ReactDomLink to={ROUTES.QUOTE_NEW}>New Quote</ReactDomLink>
                </MButton>
                {isGuidedQuotingAvailable && (
                  <MButton variant="secondary" asChild>
                    <ReactDomLink to={ROUTES.QUOTE_NEW_GUIDED}>
                      New Guided Quote
                    </ReactDomLink>
                  </MButton>
                )}
              </>
            )}
          </>
        )}
      </MPageHeader>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={quoteList?.content ?? []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={quoteList?.totalElements}
        totalPages={quoteList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={initialFilters}
        onFiltersChange={onFiltersChange}
        onRowClick={(row, _tableRow, openInNewWindow) => {
          if (row.status === 'DRAFT') {
            handleEditQuote(row.id, openInNewWindow);
          } else {
            handleReviewQuote(row.id, openInNewWindow);
          }
        }}
        emptyProps={{
          renderEmptyPlaceholder: () => <EmptyQuotesPlaceholder />,
        }}
        emptyFilterResultsProps={{ btnLabel: 'Reset Filters' }}
        resetFilter={onResetFilters}
        extraActiveFilters={extraActiveFilters}
      />
    </MPageContentContainer>
  );
};

export default QuoteListMain;
